import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
/* eslint-disable consistent-return */

import styled, { css } from 'styled-components';
import { theme, whenFocused } from "../../theme";
import { easeCurves } from "../../motion/eases";
import React, { useContext, useState, useEffect } from 'react';
import { ToggleContext } from "./ToggleContext";
import { motion } from 'framer-motion';
import VisuallyHidden from '@reach/visually-hidden';
var WIDTH = 32;
var HEIGHT = 16;
var PADDING = 2;
export var ToggleSwitch = React.forwardRef(function (_ref, forwardedRef) {
  var onFocusProp = _ref.onFocus,
      onBlurProp = _ref.onBlur,
      onChange = _ref.onChange,
      props = _objectWithoutProperties(_ref, ["onFocus", "onBlur", "onChange"]);

  var context = useContext(ToggleContext); // To enable ToggleSwitch to potentially be used outside of the Toggle
  // context provider, we fallback to allowing props to manage things
  // like checked, focused, disabled, etc. Alternatively, you can omit
  // props like tapped & focused altogether and rely on the component
  // to provide that for you.

  var _ref2 = context !== null && context !== void 0 ? context : props,
      tappedContext = _ref2.tapped,
      checked = _ref2.checked,
      disabled = _ref2.disabled,
      contextFocused = _ref2.focused; // Potentially handle cases where tapped state is local


  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      localTapped = _useState2[0],
      setLocalTapped = _useState2[1];

  var onTapStart = function onTapStart() {
    return setLocalTapped(true);
  };

  var onTapEnd = function onTapEnd() {
    return setLocalTapped(false);
  };

  var tapped = tappedContext !== null && tappedContext !== void 0 ? tappedContext : localTapped;

  var _useState3 = useState(contextFocused || false),
      _useState4 = _slicedToArray(_useState3, 2),
      focused = _useState4[0],
      setFocused = _useState4[1];

  var x = PADDING;
  var y = PADDING;
  var h = HEIGHT - PADDING * 2;
  var w = tapped && !disabled ? h + h * 0.3 : h;

  if (checked) {
    x = WIDTH - w - PADDING;
  }

  var background = theme.colors.palette.tintA30;

  if (checked) {
    background = theme.colors.palette.brandPrimary;
  }

  if (disabled) {
    background = theme.colors.palette.tintA10;
  }

  var onFocus = function onFocus(e) {
    if (onFocusProp) {
      onFocusProp(e);
    }

    if (e.target.hasAttribute('data-focus-visible-added')) {
      setFocused(true);
    }
  };

  var onBlur = function onBlur(e) {
    if (onBlurProp) {
      onBlurProp(e);
    }

    setFocused(false);
  };

  useEffect(function () {
    setFocused(contextFocused);
  }, [contextFocused]);
  var radius = HEIGHT / 2;
  return /*#__PURE__*/React.createElement(React.Fragment, null, !context && /*#__PURE__*/React.createElement(VisuallyHidden, null, /*#__PURE__*/React.createElement("input", _extends({
    type: "checkbox",
    checked: checked,
    onChange: onChange,
    disabled: disabled,
    onFocus: onFocus,
    onBlur: onBlur
  }, props))), /*#__PURE__*/React.createElement(StyledSVG, {
    $focused: focused,
    $radius: radius,
    ref: forwardedRef,
    width: WIDTH,
    height: HEIGHT,
    "aria-hidden": "true",
    disabled: disabled,
    onTapStart: onTapStart,
    onTapCancel: onTapEnd,
    onTap: onTapEnd
  }, /*#__PURE__*/React.createElement(Background, {
    x: 0,
    y: 0,
    width: WIDTH,
    height: HEIGHT,
    rx: radius,
    fill: background
  }), /*#__PURE__*/React.createElement(Circle, {
    x: x,
    y: y,
    width: w,
    height: h,
    rx: h / 2,
    fill: disabled ? theme.colors.palette.tintA20 : 'white'
  })));
});
var focusStyles = css(["box-shadow:0 0 0 1px ", ";"], theme.colors.border.interactive.focused);
var StyledSVG = styled(motion.svg).withConfig({
  displayName: "ToggleSwitch__StyledSVG",
  componentId: "vapor__sc-8lled8-0"
})(["cursor:pointer;pointer-events:", ";border-radius:", ";outline:none;", "{", ";}", ""], function (p) {
  return p.disabled && 'none';
}, function (p) {
  return "".concat(p.$radius, "px");
}, whenFocused, focusStyles, function (p) {
  return p.$focused && !p.disabled && focusStyles;
});
var Background = styled.rect.withConfig({
  displayName: "ToggleSwitch__Background",
  componentId: "vapor__sc-8lled8-1"
})(["transition:fill 0.3s ", ";"], easeCurves.primary);
var Circle = styled.rect.withConfig({
  displayName: "ToggleSwitch__Circle",
  componentId: "vapor__sc-8lled8-2"
})(["transition:fill 0.3s ", ",x 0.3s ", ",width 0.3s ", ";"], easeCurves.primary, easeCurves.primary, easeCurves.primary);